import React from "react";
import { UserPlus } from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardDescription, AppCardHeader, AppCardTitle, FieldGroup,
} from "../../ui";
import ImageField from "./ImageField";
import ListEditor from "./ListEditor";

const SPEAKER_FIELDS = [
  { key: "name", label: "Name", placeholder: "Ananya Rao", maxLength: 120 },
  { key: "title", label: "Role", placeholder: "Lead Data Scientist", maxLength: 120 },
  { key: "company", label: "Company", placeholder: "Flipkart", maxLength: 120 },
  { key: "linkedinUrl", label: "LinkedIn (optional)", placeholder: "https://www.linkedin.com/in/…", maxLength: 300 },
  { key: "bio", label: "Short bio", placeholder: "Two lines on why they are worth an hour of someone's evening.", multiline: true, full: true, maxLength: 800 },
];

const AGENDA_FIELDS = [
  { key: "time", label: "Time", placeholder: "7:00 pm", maxLength: 40 },
  { key: "title", label: "Topic", placeholder: "The analyst job market in 2026", maxLength: 200 },
  { key: "description", label: "Detail (optional)", placeholder: "What this part covers.", multiline: true, full: true, maxLength: 500 },
];

const FAQ_FIELDS = [
  { key: "question", label: "Question", placeholder: "Is this webinar free?", full: true, maxLength: 300 },
  { key: "answer", label: "Answer", placeholder: "Yes — register once and your joining link arrives on email and WhatsApp.", multiline: true, full: true, maxLength: 1500 },
];

/**
 * EventProgramSection — who is speaking, what happens when, and the questions
 * people ask before they register. All optional; an empty list is simply not
 * shown on the page.
 *
 * `hostNames` are the instructors already assigned to run the room: they are
 * usually the speakers too, so one click lists them. They stay separate on
 * purpose — the host list grants rights in the live room, this one is only
 * what the page says, and a guest speaker has no LMS account at all.
 */
const EventProgramSection = ({ form, update, previews, setPreview, hostNames = [] }) => {
  const w = form.website;
  const setWebsite = (patch) => update({ website: { ...w, ...patch } });
  const listed = new Set((w.speakers || []).map((s) => (s.name || "").trim().toLowerCase()));
  const missingHosts = hostNames.filter((n) => n && !listed.has(n.trim().toLowerCase()));

  const addHosts = () => setWebsite({
    speakers: [...(w.speakers || []), ...missingHosts.map((name) => ({ name, title: "", company: "", linkedinUrl: "", bio: "" }))].slice(0, 8),
  });

  return (
    <AppCard>
      <AppCardHeader>
        <AppCardTitle>Speakers, agenda and FAQ</AppCardTitle>
        <AppCardDescription>Shown on the event page. Leave any of them empty and that part is simply left out.</AppCardDescription>
      </AppCardHeader>
      <AppCardContent className="tw-flex tw-flex-col tw-gap-6">
        <FieldGroup legend="Speakers" hint="A face and a credible title are what turn a visitor into a registration.">
          {missingHosts.length > 0 && (
            <div>
              <AppButton type="button" size="sm" variant="ghost" onClick={addHosts}>
                <UserPlus size={16} /> Add {missingHosts.length === 1 ? missingHosts[0] : `${missingHosts.length} assigned speakers`}
              </AppButton>
            </div>
          )}
          <ListEditor
            items={w.speakers}
            onChange={(speakers) => setWebsite({ speakers })}
            fields={SPEAKER_FIELDS}
            itemLabel="Speaker"
            addLabel="Add a speaker"
            blank={{ name: "", title: "", company: "", linkedinUrl: "", bio: "", photoUrl: "", photoKey: "" }}
            max={8}
            renderRowExtra={(speaker, _i, set) => (
              <ImageField
                shape="round"
                label="Photo"
                previewUrl={(speaker.photoKey && previews[speaker.photoKey]) || speaker.photoUrl || ""}
                linkUrl={speaker.photoKey ? "" : (speaker.photoUrl || "")}
                onUploaded={({ key, url }) => { setPreview(key, url); set({ ...speaker, photoKey: key, photoUrl: "" }); }}
                onLink={(url) => set({ ...speaker, photoKey: "", photoUrl: url })}
                onClear={() => set({ ...speaker, photoKey: "", photoUrl: "" })}
              />
            )}
          />
        </FieldGroup>

        <FieldGroup legend="Agenda" hint="Times in the order they happen. Attendees plan their evening around this.">
          <ListEditor
            items={w.agenda}
            onChange={(agenda) => setWebsite({ agenda })}
            fields={AGENDA_FIELDS}
            itemLabel="Agenda item"
            addLabel="Add an agenda item"
            blank={{ time: "", title: "", description: "" }}
            max={20}
          />
        </FieldGroup>

        <FieldGroup legend="Frequently asked questions" hint="Answer the doubts that stop people registering: cost, recording, certificate, level.">
          <ListEditor
            items={w.faqs}
            onChange={(faqs) => setWebsite({ faqs })}
            fields={FAQ_FIELDS}
            itemLabel="Question"
            addLabel="Add a question"
            blank={{ question: "", answer: "" }}
            max={15}
          />
        </FieldGroup>
      </AppCardContent>
    </AppCard>
  );
};

export default EventProgramSection;
